/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import { Table } from "@tanstack/react-table";
import { SkeletonBlock } from "components/general/Skeleton";
import { useIsMobile } from "hooks/useDeviceType";
import { useKeyboardHeight } from "hooks/useKeyboardHeight";
import React, { useCallback, useEffect, useState } from "react";
import { TransactionDetail } from "types/history";
import { withIsVisible } from "utils/reactUtils";
import { Filter, FiltersInterface } from "../../config/consts";
import { useTradeHistoryFilterToggle } from "../../hooks/useTradeHistoryFilterToggle";
import { useUserTradedTokens } from "../../hooks/useUserTradedTokens";
import { ReactComponent as NoFilterResultsIcon } from "../../img/trade-history/no-filter-results.svg";
import { Token } from "../../store/global/TokenEnum";
import { FilterDropdownButton } from "./FilterDropdownButton";
import { FilterOptionRow } from "./elements/FilterOptionRow";
import { SearchTokenFilterInput } from "./elements/SearchTokenFilterInput";

export interface TokenFilterProp {
  table: Table<TransactionDetail>;
  confirmedOptions: FiltersInterface[Filter.Token];
  setConfirmedOptions: (updatedOptions: FiltersInterface[Filter.Token]) => void;
}

export const TokenFilter: React.FC<TokenFilterProp> = ({
  table,
  confirmedOptions,
  setConfirmedOptions,
}) => {
  const { localOptions, setLocalOptions, toggleLocalOptions } =
    useTradeHistoryFilterToggle<Token>();
  const [tokenSearchString, setTokenSearchString] = useState("");
  const isMobile = useIsMobile();
  const userTradedTokens = useUserTradedTokens();
  const keyboardHeight = useKeyboardHeight();
  const mobileHeight =
    keyboardHeight == null ? 336 : Math.min(window.innerHeight - keyboardHeight - 170, 336);

  useEffect(() => {
    setTokenSearchString("");
  }, [setLocalOptions]);

  useEffect(() => {
    setLocalOptions(confirmedOptions);
  }, [confirmedOptions, setLocalOptions]);

  const filterTokens = useCallback(
    (predicate: string) => {
      if (userTradedTokens != null) {
        const predicateLowercase = predicate.toLowerCase();
        return Object.values(userTradedTokens).filter(val => {
          return (
            val.ticker.toLowerCase().includes(predicateLowercase) ||
            val.name.toLowerCase().includes(predicateLowercase)
          );
        });
      }
      return undefined;
    },
    [userTradedTokens]
  );
  const filteredTokens = filterTokens(tokenSearchString);

  const TokenRows = () => {
    if (filteredTokens == null) return null;
    const alphabeticallySortedTokens = Object.values(filteredTokens).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    return (
      <>
        {alphabeticallySortedTokens.map(token => {
          return (
            <FilterOptionRow
              key={token.ticker}
              onClick={() => toggleLocalOptions(token.ticker as Token)}
              checked={localOptions?.includes(token.ticker as Token) ?? false}
            >
              <Content>
                <TokenImage src={token.iconUrl} alt={token.name} isMobile={isMobile} />
                <TokenName>{token.name}</TokenName>
              </Content>
            </FilterOptionRow>
          );
        })}
      </>
    );
  };

  return (
    <FilterDropdownButton
      filterType={Filter.Token}
      localOptions={localOptions}
      setLocalOptions={setLocalOptions}
      confirmedOptions={confirmedOptions}
      setConfirmedOptions={setConfirmedOptions}
      additionalOnClear={() => setTokenSearchString("")}
      table={table}
    >
      <SearchTokenFilterInput value={tokenSearchString} setValue={setTokenSearchString} />
      <DropdownContainer isMobile={isMobile} mobileHeight={mobileHeight}>
        <TokenRows />
        <SkeletonBlock borderRadius={4} width={100} isVisible={!filterTokens} />
        <NoFilterResult isVisible={filteredTokens != null && filteredTokens.length === 0} />
      </DropdownContainer>
    </FilterDropdownButton>
  );
};

// Sub-components

const DropdownContainer = styled.div<{ isMobile: boolean; mobileHeight: number }>`
  min-width: 258px;
  min-height: ${props => (props.isMobile ? props.mobileHeight : 42)}px;
  max-height: ${props => props.mobileHeight}px;
  overflow-y: overlay;
  ::-webkit-scrollbar {
    width: 4px;
  }
`;

const Content = styled.div`
  background: transparent;
  border: 0;
  display: flex;
  align-items: center;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const TokenName = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const TokenImage = styled.img<{ isMobile: boolean }>`
  height: ${props => (props.isMobile ? 24 : 22)}px;
  width: ${props => (props.isMobile ? 24 : 22)}px;
  margin-right: 8px;
`;

const NoFilterResult = withIsVisible(() => {
  return (
    <NoFilterResultContainer>
      <NoFilterResultsIcon width={116} height={130} />
      <NoFilterTitle>Nothing to show here.</NoFilterTitle>
      <NoFilterText>Hmmm we can’t find that token...</NoFilterText>
    </NoFilterResultContainer>
  );
});

const NoFilterResultContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 50px 0;
  overflow: hidden;
`;

const NoFilterTitle = styled.h5`
  font-size: ${props => props.theme.fontSize[5]};
  line-height: ${props => props.theme.lineHeights[4]};
  margin-top: 15px;
`;

const NoFilterText = styled.p`
  font-size: ${props => props.theme.fontSize[4]};
  font-weight: ${props => props.theme.fontWeights["work-sans-2"]};
  line-height: ${props => props.theme.lineHeights[6]};
  color: ${props => props.theme.font.secondary.default};
  margin-bottom: 0;
`;
